/**
 * Single source of truth for Content Tracker suite page layout.
 *
 * Before this file existed, `px-4` / `pb-3` / `sticky top-0 z-10 ...` and the
 * table `calc(100vh - ...)` strings were retyped in a dozen files. They agreed
 * only by luck, and the magic numbers in the calc expressions were undocumented.
 *
 * Anything laying out a suite page should import from here rather than hardcode
 * a Tailwind spacing class, so a padding change is a one-line edit.
 *
 * Scope: content-tracker frontend only. Brand Governance and Content QC have
 * their own shells and are deliberately untouched.
 */

/** Horizontal gutter for page content. Must match the Breadcrumb/FilterDrawer gutter. */
export const PAGE_X = 'px-4';

/** Margin equivalent of PAGE_X, for full-bleed dividers that need to align with content. */
export const PAGE_MX = 'mx-4';

/**
 * The vertical line every piece of page text snaps to, in px.
 *
 * Derived from where table text naturally lands, so tables need no special
 * casing:
 *
 *   PAGE_X (px-4)          16px  page gutter → card's left border
 *   card border             1px
 *   table cell px-4        16px
 *   ─────────────────────────────
 *                          33px
 *
 * The breadcrumb is pulled onto this line by absolutely positioning its sidebar
 * toggle (see Breadcrumb.tsx) — otherwise the toggle + divider push its text out
 * to 66px, which is what made the header look unaligned with the table beneath.
 */
export const CONTENT_ALIGN_PX = 33;

/** Left offset for the breadcrumb's absolutely-positioned sidebar toggle. */
export const BREADCRUMB_TOGGLE_LEFT_PX = 4;

/** Bottom gutter under page content. */
export const PAGE_PB = 'pb-3';

/**
 * Top gutter above page content, i.e. the gap between the sticky header and the
 * content card. Matches PAGE_PB so the slate background frames the card evenly
 * on all four sides.
 */
export const PAGE_PT = 'pt-3';

/** Convenience: the standard content padding used by PageContainer. */
export const PAGE_PADDING = `${PAGE_X} ${PAGE_PT} ${PAGE_PB}`;

/**
 * Sticky page-header chrome shared by every suite page header.
 *
 * z-10 sits above table content (`z-[1]` sticky theads) but below modals and
 * dropdown portals.
 *
 * Note there is deliberately no bottom padding here. The header paints a white
 * background, so padding *inside* it renders as more white and the page reads
 * as one solid block. The gap between header and content belongs to the content
 * container (PAGE_PT), where the slate page background shows through and matches
 * the gutters on the other three sides.
 */
// z-20, not z-10: content inside the page (the asset cards' expiry badges)
// uses z-10, and on a tie the later element paints on top, which drew those
// badges over the header and every dropdown it opens.
export const PAGE_HEADER_CLASS = 'sticky top-0 z-20 bg-white/95 backdrop-blur';

/**
 * Pixels reserved around a full-height table, subtracted from the viewport
 * alongside the measured header height.
 *
 *   PAGE_PT (pt-3)          = 12px  gap between header and card
 *   PAGE_PB (pb-3)          = 12px  page bottom gutter
 *   card border + rounding  = 22px  so the last row isn't flush with the edge
 */
export const PAGE_TOP_PADDING_PX = 12;
export const PAGE_BOTTOM_PADDING_PX = 12;
export const TABLE_BREATHING_ROOM_PX = 22;
export const TABLE_VIEWPORT_RESERVE_PX =
  PAGE_TOP_PADDING_PX + PAGE_BOTTOM_PADDING_PX + TABLE_BREATHING_ROOM_PX;

/**
 * maxHeight for a table scroll container that should fill the viewport below a
 * sticky header of `headerHeight` px (see `useHeaderHeight`).
 *
 * Pass 0 before the header has been measured — the table simply renders at full
 * height for one frame, which is preferable to guessing a wrong fixed number.
 */
export function tableScrollMaxHeight(headerHeight: number): string {
  return `calc(100vh - ${headerHeight}px - ${TABLE_VIEWPORT_RESERVE_PX}px)`;
}

/** Shared scrollbar styling for table scroll containers. */
export const TABLE_SCROLLBAR_STYLE = {
  scrollbarWidth: 'thin',
  scrollbarColor: '#D1D5DB #F3F4F6',
} as const;
